import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { OrganizationService } from "src/app/Services/organization.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { isVideoMediaUrl } from "src/app/utils/feed-thumbnail.util";

@Component({
  selector: "app-org-content",
  templateUrl: "./org-content.component.html",
  styleUrls: ["./org-content.component.scss"],
})
export class OrgContentComponent implements OnInit, OnDestroy {
  items: any[] = [];
  filteredItems: any[] = [];
  loading = true;
  error: string | null = null;
  organizationId: string | null = null;
  searchText = "";
  typeFilter: "All" | "Guides" | "GuidePacks" = "All";
  toogleMenuClass = false;
  private sub?: Subscription;

  get guideCount(): number {
    return this.items.filter((i) => !this.isGuidePack(i)).length;
  }

  get guidePackCount(): number {
    return this.items.filter((i) => this.isGuidePack(i)).length;
  }

  constructor(
    private organizationService: OrganizationService,
    private orgContext: OrgContextService,
    private router: Router,
    private eventEmitterService: EventEmitterService
  ) {}

  ngOnInit(): void {
    this.eventEmitterService.setDashboardMenuStyleProperty("org-content");
    if (!this.orgContext.isOrgAdminUser()) {
      this.router.navigate(["/user-dashboard/dashboard"]);
      return;
    }
    this.sub = this.orgContext.watch().subscribe(() => this.reload());
    this.reload();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  reload(): void {
    const scope = this.orgContext.contentScope();
    this.organizationId = scope.organizationId;
    if (!this.organizationId) {
      this.loading = false;
      this.items = [];
      this.applyFilters();
      this.error = "Select an organization to view content.";
      return;
    }

    this.loading = true;
    this.error = null;
    this.organizationService
      .listAdminContent(this.organizationId)
      .subscribe({
        next: (rows) => {
          this.items = Array.isArray(rows) ? rows : [];
          this.applyFilters();
          this.loading = false;
        },
        error: (err) => {
          this.items = [];
          this.applyFilters();
          this.loading = false;
          this.error =
            err?.error?.error ||
            err?.message ||
            "Unable to load organization content.";
        },
      });
  }

  applyFilters(): void {
    const term = this.searchText.trim().toLowerCase();
    this.filteredItems = this.items.filter((item) => {
      const pack = this.isGuidePack(item);
      if (this.typeFilter === "Guides" && pack) return false;
      if (this.typeFilter === "GuidePacks" && !pack) return false;
      if (!term) return true;
      return [item.title || item.Title, this.creatorName(item), this.uniqueId(item)]
        .some((v) => String(v || "").toLowerCase().includes(term));
    });
  }

  coverUrl(item: any): string | null {
    if (!item) return null;
    const cover = item.coverUrl || item.CoverUrl || "";
    const media = item.url || item.Url || "";
    const poster = item.videoThumbNailUrl || item.VideoThumbNailUrl || "";
    const trimmedCover = String(cover).trim();
    if (trimmedCover) return trimmedCover;
    const mediaTrim = String(media).trim();
    if (mediaTrim && !isVideoMediaUrl(mediaTrim)) return mediaTrim;
    const posterTrim = String(poster).trim();
    return posterTrim || null;
  }

  uniqueId(item: any): string {
    if (!item) return "N/A";
    const id = item.uniqueId || item.UniqueId || "";
    return String(id).trim() || "N/A";
  }

  creatorName(item: any): string {
    if (!item) return "Unknown";
    const name =
      item.ownerDisplayName ||
      item.OwnerDisplayName ||
      item.firstName ||
      item.FirstName ||
      "";
    const handle =
      item.ownerUserName || item.OwnerUserName || item.userName || "";
    if (String(name).trim()) return String(name).trim();
    if (String(handle).trim()) return String(handle).trim();
    return "Unknown";
  }

  isGuidePack(item: any): boolean {
    const t = item?.contentType || item?.ContentType || "";
    return String(t).toLowerCase() === "guidepack";
  }

  openItem(item: any): void {
    const contentId = item?.contentId || item?.ContentId;
    if (!contentId) return;
    if (this.isGuidePack(item)) {
      this.router.navigate(["/view-guide-pack", contentId]);
      return;
    }
    const title = item?.title || item?.Title || "flow";
    const slug = this.slugify(title) || "flow";
    this.router.navigate([`/guide/${slug}/${contentId}`]);
  }

  private slugify(value: string): string {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 80);
  }
}
